import { createSelector } from '@reduxjs/toolkit';
import { IMessageProps } from 'Components/Core';
import { ChangeEventWithData } from 'Components/Core/types';
import { camelizeObjectKeys } from 'Data/Helpers/rxjsHelpers';
import { RootState } from 'Data/Redux';
import { toIQuantity } from 'Data/ViewModels/Core/IQuantity';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { join, maxBy, uniq } from 'lodash';
import { pick } from 'ramda';
import { Selector } from 'react-redux';
import * as oPath from '../../../../Lib/Object/Path/Immutable';
import { evaluateConfig } from '../Helpers/configHelpers';
import { getLimitForPath } from '../Helpers/limitHelpers';
import { getMessagesForPath } from '../Helpers/messageHelpers';
import {
  ReadabilityData,
  SetValuePayLoad,
  WorksheetInjectedConsumerProps,
  WorksheetInjectedPropsModifier,
  WorksheetInjectedProviderData,
  WorksheetInjectedProviderProps,
} from './types';

export const getWorksheetProviderProps = (
  consumerProps: WorksheetInjectedConsumerProps,
  providerData: WorksheetInjectedProviderData,
  modifiers: Array<WorksheetInjectedPropsModifier> = []
): WorksheetInjectedProviderProps => {
  let mutableConsumerProps = { ...consumerProps };

  // Apply modifiers to consumer props
  modifiers.forEach((m) => m(mutableConsumerProps, providerData));

  // Determine props to return from provider from consumer props and provider data
  let {
    alwaysEnabled,
    disabled: consumerDisabled,
    name: consumerName,
    namePrefix,
    additionalMessagePaths = [],
    copyTo = [],
    label: consumerLabel,
    prefixName: consumerPrefixName,
    uom: consumerUom,
    labelUom: consumerLabelUom,
    messages: consumerMessages,
    measuredUsingStandaloneEquipment,
    selectors: consumerSelectors = {},
    value: consumerValue,
    onChange: consumerOnChange,
    format: consumerFormat,
  } = mutableConsumerProps;

  let {
    worksheetId,
    locked,
    setValue,
    synchronise,
    worksheetSelector,
    worksheetTypeSelector,
    worksheetConfigSelector,
    addArrayEntry,
    removeArrayEntry,
  } = providerData;

  let prefixName = consumerPrefixName || ((n) => n);
  let name = consumerName && prefixName(consumerName);

  let providerProps: WorksheetInjectedProviderProps = {
    ...mutableConsumerProps,
    prefixName,
    namePrefix,
    worksheetId,
    consumerLabel,
    createWorksheetSelector: createCreateWorksheetSelector(worksheetSelector, prefixName),
    disabled: consumerDisabled || (!alwaysEnabled && locked),
    hasInjectedProps: true,
    selectors: consumerSelectors,
    addArrayEntry,
    removeArrayEntry,
    setValue,
  };

  if (name) {
    const getConfigByNameSelector = createGetConfigByNameSelector(
      worksheetSelector,
      worksheetTypeSelector,
      worksheetConfigSelector
    );
    const configSelector = createConfigSelector(name, getConfigByNameSelector);
    const measuredUsingSelector = createMeasuredUsingSelector(
      worksheetSelector,
      configSelector,
      measuredUsingStandaloneEquipment
    );
    const readabilitySelector = createReadabilitySelector(
      worksheetSelector,
      configSelector,
      measuredUsingStandaloneEquipment,
      getConfigByNameSelector
    );
    const valueSelector = createValueSelector(worksheetSelector, name, consumerValue);
    const uomSelector = createUomSelector(configSelector, valueSelector, readabilitySelector, consumerUom);

    providerProps.selectors = {
      ...providerProps.selectors,
      config: configSelector,
      label: createLabelSelector(configSelector, uomSelector, consumerLabel, consumerLabelUom),
      messages: createMessagesSelector(
        worksheetSelector,
        [name, ...additionalMessagePaths.map((n: any) => n && prefixName(n))],
        consumerMessages
      ),
      limit: createLimitSelector(worksheetSelector, name),
      measuredUsing: measuredUsingSelector,
      format: createFormatSelector(configSelector, readabilitySelector, consumerFormat),
      value: valueSelector,
      comparisonSymbol: createComparisonSymbolSelector(worksheetSelector, name),
      onChange: createOnChangeSelector(
        uomSelector,
        consumerOnChange,
        name,
        setValue,
        synchronise,
        copyTo.map((n: any) => n && prefixName(n))
      ),
    };
  }

  return providerProps;
};

const createValueSelector = (worksheetSelector: Selector<RootState, any>, name: string, consumerValue: any) =>
  createSelector(worksheetSelector, (worksheet) => {
    if (hasNonEmptyValue(consumerValue)) return { value: consumerValue };

    const value = oPath.get(worksheet, name);
    if (value && (value.uom || hasValue(value.value))) return { value: value.value }; // Extract value from unit model
    if (value === undefined) return { value: null }; // Remove undefined
    return { value };
  });

const createComparisonSymbolSelector = (worksheetSelector: Selector<RootState, any>, name: string) =>
  createSelector(worksheetSelector, (worksheet) => {
    const value = oPath.get(worksheet, name);
    if (value && value.comparisonSymbol) {
      return { comparisonSymbol: value.comparisonSymbol }; // Extract value from ranged quantity model
    }
    return { comparisonSymbol: null };
  });

/**
 * Creates the createWorksheetSelector property for a consumer given its prefixName function.
 */
const createCreateWorksheetSelector =
  (worksheetSelector: Selector<RootState, any>, prefixName: any) =>
  /**
   * Selects a value from the worksheet for the given path
   * @param {string} path
   * @param {boolean} usePrefix - if true, path will be prefixed the same as the component using this selector (true by default)
   */
  (path: string = '', usePrefix: boolean = true) =>
    createSelector(worksheetSelector, (worksheet) => {
      return oPath.get(worksheet, usePrefix ? prefixName(path) : path);
    });

const createGetConfigByNameSelector = (
  worksheetSelector: Selector<RootState, any>,
  worksheetTypeSelector: Selector<RootState, any>,
  worksheetConfigSelector: Selector<RootState, any>
) =>
  createSelector(
    worksheetSelector,
    worksheetTypeSelector,
    worksheetConfigSelector,
    (worksheet, worksheetType, config) => (name: string) =>
      evaluateConfig(config, name, { name, worksheet, worksheetType })
  );

const createConfigSelector = (name: string, getConfigByNameSelector: Selector<RootState, (name: string) => any>) =>
  createSelector(getConfigByNameSelector, (getConfigByName) => getConfigByName(name));

const createUomSelector = (
  configSelector: Selector<RootState, any>,
  valueSelector: Selector<RootState, any>,
  readabilitySelector: Selector<RootState, ReadabilityData | undefined>,
  consumerUom: string | undefined
) =>
  createSelector(
    configSelector,
    valueSelector,
    readabilitySelector,
    (config, value, readability) => (readability?.uom || consumerUom || config?.uom || value?.uom) as string | undefined
  );

const createLabelSelector = (
  configSelector: Selector<RootState, any>,
  uomSelector: Selector<RootState, string | undefined>,
  consumerLabel: string | undefined,
  consumerLabelUom: string | undefined
) =>
  createSelector(configSelector, uomSelector, (config, uom) => {
    let label = consumerLabel || config?.label;
    let labeluom = consumerLabelUom || config?.labelUom || uom;
    label = label && labeluom ? `${label} (${labeluom})` : label;
    return { label };
  });

/**
 * Creates a selector that obtains the readability properties for the field this component represents
 */
const createReadabilitySelector = (
  worksheetSelector: Selector<RootState, any>,
  configSelector: Selector<RootState, any>,
  measuredUsingStandaloneEquipment: string | undefined,
  getConfigByNameSelector: Selector<RootState, (name: string) => any>
) =>
  createSelector(
    worksheetSelector,
    configSelector,
    getConfigByNameSelector,
    (state: RootState) => state.equipment.equipmentData,
    (worksheet, config, getConfigByName, equipmentData) => {
      // Check if this component represents a measured field
      if (config?.measuredUsingName || measuredUsingStandaloneEquipment) {
        return getReadabilityData(
          worksheet,
          config?.measuredUsingName,
          getConfigByName(config?.measuredUsingName),
          measuredUsingStandaloneEquipment
            ? oPath.get(equipmentData, `${measuredUsingStandaloneEquipment}.data`)
            : undefined
        );
      }

      // Check if this component represents a field derived from other measurements
      if (config?.derivedFromMeasurements && hasNonEmptyValue(config?.derivedFromMeasurements)) {
        const measuringEquipmentNames = (config?.derivedFromMeasurements as string[])
          .map((m) => getConfigByName(m)?.measuredUsingName)
          .filter((m) => m);
        const measurementReadabilities = uniq(measuringEquipmentNames)
          .map((n) => getReadabilityData(worksheet, n, getConfigByName(n)))
          .filter((r) => r.readability);

        if (uniq(measurementReadabilities.map((r) => r.uom)).length > 1) {
          throw new Error(
            `Cannot determine composite readability from measurements ${join(
              config?.derivedFromMeasurements,
              ', '
            )}. The measuring equipment (${join(measuringEquipmentNames, ', ')}) have incompatible units (${join(
              uniq(measurementReadabilities.map((r) => r.uom)),
              ', '
            )}).`
          );
        }
        // We use a very unsophisticated approach here - just get the readability for each constituent measurement
        // and use the least precise.
        // This is only intended for use when results are directly derived from measurements by additive operators.
        // Where measurements are only part of a more complicated calculation, we will usually be using a set precision for the standard.
        return maxBy(measurementReadabilities, (r) => r.readability);
      }

      // No readability properties
      return undefined;
    }
  );

/**
 * Gets the readability values for the measuring equipment at measuredUsingName
 * given the (already selected) worksheet / config
 *
 * @param worksheet Worksheet model
 * @param measuredUsingName Name referring to the measuring equipment (i.e. the path where it is imported in the worksheet model)
 * @param measuredUsingConfig Configuration for the measuring equipment (by the given name)
 * @param measuredUsingOverride Provide this argument when we are overriding the equipment at measuredUsingName
 * with a reference to an external equipment item
 **/
const getReadabilityData = (
  worksheet: any,
  measuredUsingName: string,
  measuredUsingConfig: any,
  measuredUsingOverride?: any
) => {
  const defaultReadability: ReadabilityData = {
    readability: measuredUsingConfig?.defaultReadability,
    uom: measuredUsingConfig?.defaultUom,
    supportedUoms: measuredUsingConfig?.supportedUoms ?? [measuredUsingConfig?.defaultUom] ?? undefined,
  };

  let equipmentReadability: ReadabilityData = {};

  if (measuredUsingOverride) {
    equipmentReadability = camelizeObjectKeys(pick(['Readability', 'Uom'], measuredUsingOverride ?? {}));
  } else if (measuredUsingName) {
    equipmentReadability = camelizeObjectKeys(
      pick(['Readability', 'Uom'], oPath.get(worksheet, `${measuredUsingName}.Results`) ?? {})
    );
  }

  // Only use the measuring equipment's readability when one of the following is true
  //    - readability has a value and the units match the expected default
  //    - readability has a value and both the default uom and equipment uom are undefined
  //          * allows for the case where the readability is unitless
  // Otherwise fallback to the default readability

  if (
    equipmentReadability.readability &&
    ((equipmentReadability.uom === undefined && defaultReadability.uom === undefined) ||
      (equipmentReadability.uom &&
        (defaultReadability.uom === undefined || defaultReadability.supportedUoms?.includes(equipmentReadability.uom))))
  ) {
    return equipmentReadability;
  } else {
    return defaultReadability;
  }
};

const createFormatSelector = (
  configSelector: Selector<RootState, any>,
  readabilitySelector: Selector<RootState, ReadabilityData | undefined>,
  consumerFormat: any | undefined
) =>
  createSelector(configSelector, readabilitySelector, (config, readability) => ({
    format: consumerFormat || (readability ? { nearest: readability?.readability } : config?.format),
  }));

const createMeasuredUsingSelector = (
  worksheetSelector: Selector<RootState, any>,
  configSelector: Selector<RootState, any>,
  measuredUsingStandaloneEquipment: string | undefined
) =>
  createSelector(
    worksheetSelector,
    configSelector,
    (state: RootState) => state.equipment.equipmentData,
    (worksheet, config, equipment) => {
      if (measuredUsingStandaloneEquipment) {
        return { measuredUsing: oPath.get(equipment, `${measuredUsingStandaloneEquipment}.data`) };
      }
      if (config?.measuredUsingName) {
        return { measuredUsing: oPath.get(worksheet, config?.measuredUsingName) };
      }
    }
  );

const createMessagesSelector = (
  worksheetSelector: Selector<RootState, any>,
  paths: string[],
  consumerMessages: IMessageProps[] | undefined = []
) =>
  createSelector(worksheetSelector, (worksheet) => ({
    messages: paths
      .map((path) => getMessagesForPath(worksheet, path))
      .flat(1)
      .concat(consumerMessages),
  }));

const createLimitSelector = (worksheetSelector: Selector<RootState, any>, name: any) =>
  createSelector(worksheetSelector, (worksheet) => ({ limit: getLimitForPath(worksheet, name) }));

const createOnChangeSelector = (
  uomSelector: Selector<RootState, string | undefined>,
  consumerOnChange: ((_: ChangeEventWithData) => void) | undefined,
  name: string,
  setValue: (update: SetValuePayLoad) => void,
  synchronise: () => void,
  copyTo: Array<string> | undefined
) =>
  createSelector(uomSelector, (uom) => ({
    onChange: consumerOnChange
      ? // Consumer onChange used without modification
        consumerOnChange
      : // Default onChange - setValue and synchronise depending on data package on event
        (evt: ChangeEventWithData) => {
          let value = evt.data?.value;

          if (uom && hasValue(value)) {
            // Quantity fields have a UOM, so the value they emit should be a bit different to other fields. Doing it here because
            // individual inputs shouldn't really need to know about quantities, they just emit a value. It's also convenient to
            // do here because we have the config property 'uom'.
            value = toIQuantity(value, uom);
          }

          if (name !== undefined && value !== undefined) {
            setValue({ name, value });
            copyTo?.forEach((dest) => setValue({ name: dest, value }));
          }
          if (evt.data?.submitValues) synchronise();
        },
  }));
